/**
 * ResizableImage · Feb 2026 · R135
 *
 * A Tiptap Image extension that renders images with a drag-to-resize
 * handle at the bottom-right corner. The width is persisted as an
 * inline `style="width:XXXpx"` attribute so downstream HTML consumers
 * (email clients, PDF renderers) honour the size we picked.
 *
 * Behaviour:
 *   • Click the image → the resize handle appears at the bottom-right.
 *   • Drag the handle → width follows the pointer, preserving aspect ratio.
 *   • Release → the size is saved into the editor state.
 *   • The image stays selected while dragging so the handle doesn't flicker.
 */
import { useEffect, useRef, useState } from "react";
import TiptapImage from "@tiptap/extension-image";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

const MIN_WIDTH = 40;
const MAX_WIDTH = 1400;

function ImageComponent({ node, updateAttributes, selected }) {
  const { src, alt, title, width } = node.attrs;
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setHovered(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  const showHandle = selected || hovered;

  const onResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    if (!img) return;
    const startX = e.clientX;
    const startWidth = img.getBoundingClientRect().width;
    const naturalW = img.naturalWidth || startWidth;
    const naturalH = img.naturalHeight || 1;
    const ratio = naturalH / (naturalW || 1);

    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(startWidth + delta)));
      // Instant visual feedback — DOM only, don't spam updateAttributes.
      img.style.width = `${next}px`;
      img.style.height = `${Math.round(next * ratio)}px`;
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const finalWidth = parseInt(img.style.width, 10) || startWidth;
      updateAttributes({ width: finalWidth });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const inlineStyle = width
    ? { width: `${width}px`, height: "auto", maxWidth: "100%" }
    : { maxWidth: "100%", height: "auto" };

  return (
    <NodeViewWrapper
      as="div"
      className="my-3 relative inline-block group"
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="srt-image-node"
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt || ""}
        title={title || ""}
        style={inlineStyle}
        className={`rounded ${selected ? "ring-2 ring-primary ring-offset-2" : ""}`}
        draggable={false}
        data-testid="srt-image"
      />
      {showHandle && (
        <>
          <div
            aria-label="Resize image"
            title="Drag to resize"
            onMouseDown={onResizeStart}
            className="absolute -right-1.5 -bottom-1.5 w-4 h-4 bg-primary border-2 border-background cursor-nwse-resize shadow"
            style={{ zIndex: 2 }}
            data-testid="srt-image-resize-handle"
          />
          <div
            className="absolute -top-6 right-0 text-[10px] font-mono bg-primary text-primary-foreground px-1.5 py-0.5"
            style={{ zIndex: 2 }}
            data-testid="srt-image-size-badge"
          >
            {width ? `${width}px` : "auto"}
          </div>
        </>
      )}
    </NodeViewWrapper>
  );
}

const ResizableImage = TiptapImage.extend({
  name: "image",
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const styleW = el.style?.width;
          if (styleW && styleW.endsWith("px")) {
            const n = parseInt(styleW, 10);
            return Number.isFinite(n) ? n : null;
          }
          const attrW = el.getAttribute("width");
          if (attrW && !attrW.endsWith("%")) {
            const n = parseInt(attrW, 10);
            return Number.isFinite(n) ? n : null;
          }
          return null;
        },
        renderHTML: (attrs) => {
          if (!attrs.width) return {};
          // Persist as inline style so email clients + PDF renderers honour it.
          return {
            width: String(attrs.width),
            style: `width:${attrs.width}px;height:auto;max-width:100%`,
          };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageComponent);
  },
});

export default ResizableImage;
